/** Which debuffs a nameplate shows, addon-style: your own debuffs plus crowd control from anyone. */
import type { AuraInstance, ContentRegistry, DeepReadonly, UnitId } from '@keystone/sim';

export interface PlateAura {
  iid: string;
  /** Aura and applying ability (icon lookup: iconUrl(auraId) ?? iconUrl(abilityId)). */
  auraId: string;
  abilityId: string;
  label: string;
  name: string;
  color: string;
  remainingMs: number | null;
  /** 0..1 of the current application elapsed (drives the cooldown sweep). */
  elapsedFrac: number;
  stacks: number;
  cc: boolean;
  /** Inside the pandemic window: refreshing now loses nothing. */
  refreshable: boolean;
}

export const MAX_PLATE_AURAS = 6;
/** Pandemic carries over up to 30% of the new duration. */
const PANDEMIC_FRAC = 0.3;

const labelCache = new Map<string, string>();
/** Initials of the aura name, e.g. "Flame Shock" -> "FS" (cached per name). */
export function auraLabel(name: string): string {
  let l = labelCache.get(name);
  if (l === undefined) {
    const words = name.split(/[\s-]+/).filter(Boolean);
    l = words.length === 1 ? words[0]!.slice(0, 2).toUpperCase() : words.slice(0, 2).map((w) => w[0]!.toUpperCase()).join('');
    labelCache.set(name, l);
  }
  return l;
}

function blank(): PlateAura {
  return { iid: '', auraId: '', abilityId: '', label: '', name: '', color: '', remainingMs: null, elapsedFrac: 0, stacks: 1, cc: false, refreshable: false };
}

function byRelevance(x: PlateAura, y: PlateAura): number {
  // CC first (it matters most at a glance), then soonest to expire.
  return Number(y.cc) - Number(x.cc) || (x.remainingMs ?? Infinity) - (y.remainingMs ?? Infinity);
}

/**
 * Allocation-free variant for per-frame use: fills `out` with entries taken from `pool` (grown
 * on demand, then reused), sorted CC first then soonest to expire, capped at MAX_PLATE_AURAS.
 * Returns the count. Entries are overwritten on the next call.
 */
export function collectPlateAuras(
  auras: readonly DeepReadonly<AuraInstance>[],
  playerId: UnitId | null,
  now: number,
  content: ContentRegistry,
  pool: PlateAura[],
  out: PlateAura[],
): number {
  out.length = 0;
  for (let i = 0; i < auras.length; i++) {
    const a = auras[i]!;
    const def = content.aura(a.auraId);
    if (!def || def.kind !== 'debuff' || def.flags?.hidden) continue;
    const cc = !!def.cc;
    const mine = playerId !== null && a.casterId === playerId;
    if (!mine && !cc) continue;
    const remainingMs = a.expiresAt === null ? null : Math.max(0, a.expiresAt - now);
    const p = pool[out.length] ?? (pool[out.length] = blank());
    p.iid = String(a.iid);
    p.auraId = a.auraId;
    p.abilityId = a.abilityId;
    p.name = def.name;
    p.label = auraLabel(def.name);
    p.color = content.ability(a.abilityId)?.ui?.color ?? (cc ? '#c0392b' : '#8e7cc3');
    p.remainingMs = remainingMs;
    p.elapsedFrac = remainingMs === null || a.durationMs <= 0 ? 0 : Math.min(1, 1 - remainingMs / a.durationMs);
    p.stacks = a.stacks;
    p.cc = cc;
    p.refreshable = mine && !!def.pandemic && remainingMs !== null && remainingMs <= a.durationMs * PANDEMIC_FRAC;
    out.push(p);
  }
  if (out.length > 1) out.sort(byRelevance);
  if (out.length > MAX_PLATE_AURAS) out.length = MAX_PLATE_AURAS;
  return out.length;
}

/** Convenience (allocating) form: fresh entries, same filter and order. */
export function plateAuras(auras: readonly DeepReadonly<AuraInstance>[], playerId: UnitId | null, now: number, content: ContentRegistry): PlateAura[] {
  const out: PlateAura[] = [];
  collectPlateAuras(auras, playerId, now, content, [], out);
  return out;
}

export function formatAuraTime(ms: number | null): string {
  if (ms === null) return '';
  const s = ms / 1000;
  if (s >= 60) return `${Math.ceil(s / 60)}m`;
  if (s >= 10) return `${Math.ceil(s)}`;
  return s.toFixed(1);
}
